import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Users, History, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import AnimatedBadge from '../ui/AnimatedBadge';

const SLIDES = [
  {
    image: "/images/history/historical.png",
    title: "Desde 1969",
    subtitle: "Grupo Etesco & JWCosta",
    icon: <History className="text-white" />
  },
  {
    image: "/images/history/modern.png",
    title: "Inovação Digital",
    subtitle: "Tecnologia & Eficiência",
    icon: <Award className="text-white" />
  },
  {
    image: "/images/history/human.png",
    title: "DNA Humano",
    subtitle: "Pessoas atendendo Pessoas",
    icon: <Heart className="text-white" />
  }
];

export default function HistorySection() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + SLIDES.length) % SLIDES.length);

  return (
    <section id="history" className="w-full bg-background py-24 relative overflow-hidden border-t border-foreground/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          
          {/* Slider Left */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative z-10 rounded-[3.5rem] overflow-hidden border border-foreground/10 aspect-[4/5] shadow-2xl group">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, scale: 1.1, filter: 'blur(10px)' }}
                  animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, scale: 0.95, filter: 'blur(10px)' }}
                  transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0"
                >
                  <img 
                    src={SLIDES[currentIndex].image} 
                    alt={SLIDES[currentIndex].title} 
                    className="w-full h-full object-cover grayscale-[10%] group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent"></div>
                </motion.div>
              </AnimatePresence>

              {/* Slide Content Overlay */}
              <div className="absolute bottom-10 left-10 right-10 z-20">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="bg-foreground/5 backdrop-blur-2xl border border-foreground/10 p-8 rounded-3xl"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-orange-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-orange-600/20">
                        {SLIDES[currentIndex].icon}
                      </div>
                      <div>
                        <h4 className="text-white font-bold text-xl">{SLIDES[currentIndex].title}</h4>
                        <p className="text-slate-100/60 text-[10px] uppercase tracking-[0.2em] font-extrabold">{SLIDES[currentIndex].subtitle}</p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Slider Controls */}
              <div className="absolute top-1/2 -translate-y-1/2 left-4 right-4 flex justify-between z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <button 
                  onClick={prevSlide}
                  className="w-12 h-12 rounded-full bg-background/20 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-orange-600 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  onClick={nextSlide}
                  className="w-12 h-12 rounded-full bg-background/20 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-orange-600 transition-colors"
                >
                  <ChevronRight size={24} />
                </button>
              </div>

              {/* Indicators */}
              <div className="absolute top-8 right-8 flex gap-2 z-30">
                {SLIDES.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentIndex(idx)}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      currentIndex === idx ? 'w-8 bg-orange-500' : 'w-2 bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Decorative background element */}
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-orange-600/10 rounded-full blur-3xl -z-10"></div>
          </motion.div>

          {/* Content Right */}
          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="mb-8">
              <AnimatedBadge>
                A Melhor Equipe de Contadores
              </AnimatedBadge>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-foreground tracking-tight mb-8 leading-[1.4]">
              A Solidez de 1969 <br /> com o Olhar no Amanhã.
            </h2>
            <p className="text-lg text-slate-400 font-medium mb-8 leading-relaxed">
              Nascemos como Etesco Escritório Técnico Contábil, uma empresa familiar focada em credibilidade. Em 2015, evoluímos para <span className="text-foreground">**JWCosta Soluções Contábeis**</span>, herdando meio século de excelência e humanidade do nosso fundador, <span className="text-orange-500 font-bold">José Walter da Costa</span>.
            </p>
            
            <p className="text-slate-500 mb-10 italic border-l-2 border-orange-500 pl-6 py-2">
              "Mantemos acesa a chama do empreendedorismo, da honestidade e da excelência herdada de nossa história."
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-foreground font-bold">
                  <Users size={18} className="text-orange-500" />
                  <span>Equipe Familiar</span>
                </div>
                <p className="text-slate-500 text-xs uppercase tracking-wider font-bold">Familiar, mas Profissional</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-foreground font-bold">
                  <Heart size={18} className="text-orange-500" />
                  <span>DNA Humano</span>
                </div>
                <p className="text-slate-500 text-xs uppercase tracking-wider font-bold">Respiramos Contabilidade</p>
              </div>
            </div>

            <a 
              href="https://www.instagram.com/jwcosta_contabilidade/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-fit px-10 py-5 bg-orange-600 hover:bg-orange-500 text-white font-black rounded-full transition-all duration-300 uppercase tracking-widest text-[10px] inline-block text-center shadow-xl shadow-orange-600/20"
            >
              Nos Acompanhe nas Redes
            </a>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

